import useUserVarStore from "@app/store/useUserVarStore";
import { GalleryCarouselTypes } from "@app/types/Opensearch.d";
import ArticleFollowTopic from "@components/Blocks/ArticleFollowTopic/ArticleFollowTopic";
import ArticleStickyBar from "@components/Blocks/ArticleStickyBar/ArticleStickyBar";
import AuthorCard from "@components/Blocks/Card/Author/AuthorCard";
import ReactionToolbar from "@components/Blocks/ReactionToolbar/ReactionToolbar";
import { SocialVariant } from "@components/Blocks/ShareSocial/ShareSocial";
import ShareSocialNew from "@components/Blocks/ShareSocial/ShareSocialNew";
import ArticleCarousel from "@components/Layouts/Carousel/ArticleCarousel";
import { ENABLE_MYBH } from "@config/followTopic";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import BeyondWordsPlayer from "@elements/BeyondWordsPlayer/BeyondWordsPlayer";
import {
  ARTICLE_PAYWALL_CLASS,
  ARTICLE_REGWALL_CLASS,
} from "@elements/MetaTags/MetaTagsConstants";
import FollowTopicsToastMessage from "@elements/ToastMessage/FollowTopicsToastMessage";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import Link from "@elements/Typography/Link";
import { StandFirst } from "@elements/Typography/Paragraph";
import { RenderTags } from "@elements/Typography/Tags";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import { shouldShowTTSPlayer } from "@helper/showTTS";
import {
  findStyleBySectionAndSubCategory,
  isArrayWithElements,
} from "@helper/utils";
import { useArticleReactions } from "@hooks/useArticleReactions";
import useFollowTags from "@hooks/useFollowTags";
import useMySPHLightbox from "@hooks/useMySPHLightbox";
import { NoSSR } from "@sphtech/web2-core/components";
import { LabelCardIconVariety } from "@src/app/components/Elements/Icon/LabelCardIcon";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import classNames from "classnames";
import { useMemo, useState } from "react";

import ArticleElementWrapper from "../Components/ArticleElementWrapper";
import ContentAccessLabel from "../Components/ContentAccessLabel";

export type ArticleContentCardNewProps = {
  mainArticle: ProcessedArticleData;
  pageNumber: number;
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  index?: number;
  visibleArticleId?: string;
};

export default function ArticleContentCardNew({
  mainArticle,
  pageNumber,
  isInfiniteScrollArticle = false,
  gsChannels,
  articleSlotTarget,
  index,
  visibleArticleId,
}: ArticleContentCardNewProps) {
  const { userToken } = useUserVarStore();
  const {
    sectionName,
    uniqueSectionName,
    title,
    tags,
    authors,
    date,
    datetime,
    time,
    articleBody,
    standFirst,
    contentAccess,
    urlPath,
    imageGallery,
    relatedArticles,
    attachments,
    id,
    sourceId,
    linkToSection,
    adsUniqueSectionName,
    campaignReference,
  } = mainArticle;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: datetime,
    uniqueSectionName,
  });

  const { lightbox } = useMySPHLightbox();
  const { visitorId, isFollowing, toggleFollow, loadingTopicId, showToast } =
    useFollowTags({
      onRequireLogin: () => lightbox?.initiateLogin(),
    });

  const socialIconList = ["facebook", "telegram", "plainWhatsapp", "newsPost"];
  const pageUrl = `${urlPath}`;
  const renderItems = imageGallery.filter(
    (item) => item.type !== GalleryCarouselTypes.UNKNOWN,
  );
  const sectionColor = mainArticle.parentSection
    ? findStyleBySectionAndSubCategory(mainArticle.parentSection)
        ?.selectedColor || ""
    : findStyleBySectionAndSubCategory(sectionName || "")?.selectedColor || "";
  const showTTSPlayer = shouldShowTTSPlayer(datetime, contentAccess);
  const isCampaign = Array.isArray(campaignReference)
    ? campaignReference.length > 0
    : !!(campaignReference?.href && campaignReference.title);
  const elementLink = isCampaign
    ? `mata-mata/${campaignReference?.href.split("/").at(-1)}`
    : linkToSection;

  const sectionTitleProps = {
    Element: Link,
    className:
      "text-[16px] lg:text-[12px] lg:font-semibold mb-0 leading-[120%]" +
      (isCampaign ? " text-blue-300 hover:text-blue-700 hover:underline" : ""),
    sectionName: isCampaign ? campaignReference?.title : sectionName,
    parentSection: isCampaign ? sectionName : mainArticle.parentSection,
    iconStyle: "hidden" as const,
  };

  const [isStickyVisible, setIsStickyVisible] = useState(false);

  const isMyBH = ENABLE_MYBH;
  const firstTag = tags?.[0];

  const renderAuthors = useMemo(() => {
    return (
      <>
        {isArrayWithElements(authors) && authors.some((a) => a.hasProfile) && (
          <div className="flex flex-row gap-3 flex-wrap">
            {authors
              .filter((author) => author.hasProfile)
              .map((author, index) => (
                <AuthorCard
                  key={index}
                  author={author}
                  displayHeight={44}
                  displayWidth={44}
                  imageWrapper="w-[46px] rounded-[55px] border border-white-600"
                  authorCardWrapper="gap-[5px]"
                  nameClass="text-[12px] lg:text-[12px] font-medium text-blue-400 leading-[150%]"
                  hasEmail={false}
                  forAuthorListing={false}
                />
              ))}
          </div>
        )}
      </>
    );
  }, [authors]);

  const renderMetaSection = () => (
    <div
      className={
        isMyBH
          ? "flex w-full gap-3 pt-[3px] mb-6 lg:mb-8"
          : "flex w-full gap-3 mb-2"
      }
    >
      <div
        className={
          isMyBH
            ? "flex flex-col-reverse flex-grow-no-shrink gap-1 justify-center"
            : "flex flex-col flex-grow-no-shrink gap-3 justify-center"
        }
      >
        <div className="flex">
          <Date
            date={date}
            time={time}
            variant="or"
            className="text-[14px] font-semimedium leading-[150%] text-black-700"
            dateTime={datetime}
          />
        </div>
        {renderAuthors}
      </div>

      <ReactionToolbar
        articleId={id}
        showReactions={showReactions}
        reactions={reactions}
        userReactions={userReactions}
        reactionsError={reactionsError}
        handleReactionClick={handleReactionClick}
      />
    </div>
  );

  return (
    <div
      key={index}
      id={`${id}-article-body`}
      className={classNames({ v_showStickyBar: isStickyVisible })}
    >
      <FollowTopicsToastMessage forceShowToast={showToast} />
      <ArticleStickyBar
        article={mainArticle}
        pageNumber={pageNumber}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        onVisibilityChange={setIsStickyVisible}
        isActive={visibleArticleId === id}
      />
      <ArticleElementWrapper>
        <div
          className={`flex items-center pb-3 mb-2 border-b-2 border-${sectionColor}`}
          data-testid="section-share-wrapper"
        >
          <SectionTitleV2 {...sectionTitleProps} elementLink={elementLink} />
        </div>
        <div
          className={classNames(
            "flex flex-col gap-2 mb-2",
            contentAccess === LabelCardIconVariety.REGISTERED_USER &&
              ARTICLE_REGWALL_CLASS,
            contentAccess === LabelCardIconVariety.PREMIUM &&
              ARTICLE_PAYWALL_CLASS,
          )}
        >
          <ContentAccessLabel contentAccess={contentAccess} showIcon={false} />
          <Heading
            Element="h1"
            className="font-secondary leading-[120%] text-black-800 text-[22px] lg:text-[38px] font-medium article-headline"
            sourceId={sourceId}
          >
            {title}
          </Heading>
          {standFirst && (
            <StandFirst className="text-xs lg:text-xs font-primary font-[500] leading-[150%] text-grey-350">
              {standFirst}
            </StandFirst>
          )}
        </div>
        {!isMyBH && (
          <>
            {!showTTSPlayer && (
              <RenderTags tagsWrapper="my-2 gap-[10px]" tags={tags} />
            )}
            {renderMetaSection()}
          </>
        )}
        <ArticleStickyBar
          article={mainArticle}
          pageNumber={pageNumber}
          isStatic={true}
          gsChannels={gsChannels}
          articleSlotTarget={articleSlotTarget}
        />
        {showTTSPlayer && (
          <BeyondWordsPlayer
            cueId={id}
            title={title}
            articleId={id}
            isInfiniteScrollArticle={isInfiniteScrollArticle}
            visibleArticleId={visibleArticleId}
          />
        )}
        {isArrayWithElements(renderItems) && (
          <ArticleCarousel
            renderItems={renderItems}
            sectionName={sectionName}
            wrapperClass="max-w-[860px]"
          />
        )}
        {isMyBH && (
          <div className="flex flex-col gap-2 lg:gap-4">
            {firstTag && (
              <ArticleFollowTopic
                label={firstTag.name ?? ""}
                topicId={firstTag.id ?? ""}
                onFollow={toggleFollow}
                onUnfollow={toggleFollow}
                isFollowing={isFollowing(firstTag.id ?? "")}
                isLoading={loadingTopicId === firstTag.id}
                loggedIn={visitorId !== ""}
              />
            )}

            {renderMetaSection()}
          </div>
        )}
      </ArticleElementWrapper>

      <ArticleParagraph
        freeArticle={mainArticle}
        articleId={id}
        contentAccess={contentAccess}
        articleBody={articleBody}
        uniqueSectionName={uniqueSectionName}
        relatedArticles={relatedArticles}
        pageNumber={pageNumber}
        isInfiniteScrollArticle={isInfiniteScrollArticle}
        gsChannels={gsChannels}
        articleSlotTarget={articleSlotTarget}
        visibleArticleId={visibleArticleId}
        adsUniqueSectionName={adsUniqueSectionName}
        attachments={attachments}
        tags={tags}
        showReactions
        reactions={reactions}
        handleReactionClick={handleReactionClick}
        userReactions={userReactions}
      />

      <ArticleElementWrapper>
        <div
          className="flex self-end items-center justify-end my-6 lg:my-8 track-impression"
          data-section-name="end-of-article"
        >
          <NoSSR>
            {socialIconList.map((variant, index) => (
              <ShareSocialNew
                variant={variant as SocialVariant}
                pagePath={pageUrl}
                pageTitle={title}
                dateCreated={datetime}
                key={index}
              />
            ))}
          </NoSSR>
        </div>
      </ArticleElementWrapper>
    </div>
  );
}
